import { useState } from "react";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Globe, Search, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function ExternalSearchDialog({ onImported }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [importing, setImporting] = useState(null);

    const run = async (e) => {
        e?.preventDefault();
        if (!query.trim()) return;
        setLoading(true);
        try {
            const { data } = await api.get("/external-drugs/search", { params: { query } });
            setResults(data.results || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "External search failed");
        } finally { setLoading(false); }
    };

    const doImport = async (r) => {
        setImporting(r.external_id);
        try {
            await api.post("/external-drugs/import", {
                name: r.name,
                company: r.company,
                development_stage: r.development_stage,
                clinical_area: r.clinical_area,
                indication: r.indication,
                mechanism: r.mechanism,
                uk_status: r.uk_status,
                sources: r.sources || [],
            });
            toast.success(`Imported ${r.name}`);
            setResults((prev) => prev.filter((x) => x.external_id !== r.external_id));
            onImported && onImported();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Import failed");
        } finally { setImporting(null); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-[#E2E8F0] bg-white" data-testid="external-search-trigger">
                    <Globe className="w-4 h-4" /> UK registry search
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl bg-[#F8F7F4]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-[#1A4331]">Search the UK pharma registry</DialogTitle>
                    <DialogDescription>
                        Queries ClinicalTrials.gov for UK-located studies. Import results into your workspace to run AI intelligence on them.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={run} className="flex gap-2" data-testid="external-search-form">
                    <Input placeholder="Drug name, mechanism, or condition (e.g. osimertinib)"
                        value={query} onChange={(e) => setQuery(e.target.value)}
                        className="bg-white border-[#E2E8F0] h-11"
                        data-testid="external-query-input" />
                    <Button type="submit" disabled={loading} className="bg-[#1A4331] hover:bg-[#123022] text-white gap-2 h-11" data-testid="external-search-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        Search
                    </Button>
                </form>
                <div className="max-h-96 overflow-y-auto space-y-2">
                    {results === null && (
                        <p className="text-sm text-[#8A9A86] text-center py-8">Enter a term above to search.</p>
                    )}
                    {results && results.length === 0 && (
                        <p className="text-sm text-[#8A9A86] text-center py-8">No new matches. Try another term.</p>
                    )}
                    {results && results.map((r) => (
                        <div key={r.external_id} className="p-3 rounded-md bg-white border border-[#E2E8F0] flex items-start gap-3" data-testid={`external-result-${r.external_id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#1E293B] truncate">{r.name}</p>
                                <p className="text-xs text-[#475569]">{r.company} — {r.indication}</p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <Badge className="bg-[#F1F5F9] text-[#475569] border-0 hover:bg-[#F1F5F9]">{r.development_stage}</Badge>
                                    <Badge className="bg-[#F1F5F9] text-[#475569] border-0 hover:bg-[#F1F5F9]">{r.external_id}</Badge>
                                </div>
                            </div>
                            <Button size="sm" onClick={() => doImport(r)} disabled={importing === r.external_id}
                                className="bg-[#1A4331] hover:bg-[#123022] text-white gap-1 shrink-0"
                                data-testid={`import-${r.external_id}`}>
                                {importing === r.external_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                                Import
                            </Button>
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <p className="text-xs text-[#8A9A86] mr-auto">Source: ClinicalTrials.gov v2 API (UK location filter)</p>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
